import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Group,
  Loader,
  Pagination,
  Paper,
  Popover,
  ScrollArea,
  Select,
  Stack,
  Tabs,
  Text,
  UnstyledButton,
  useMantineTheme,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { FaBell } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../contexts/language.context";
import { GET_ALL_COMPANIES } from "../services/companies";
import { GET_NOTIFICATIONS, NotificationItem, READ_NOTIFICATION } from "../services/notifications";

type NotificationStatusFilter = "all" | "unread" | "read";

const DEFAULT_LIMIT = 10;

const getCompanyLabel = (company: any) => company?.name || company?.title || company?.label || company?.id || company?._id || "";

const resolveNotificationHref = (notification: NotificationItem) => {
  const payloadUrl = notification?.payload_jsonb?.url || notification?.payload_jsonb?.href || notification?.payload_jsonb?.path;
  if (payloadUrl) return payloadUrl;

  const referenceType = `${notification?.reference_type || ""}`.toLowerCase();
  const referenceId = notification?.reference_id;

  if (!referenceType) return null;
  if (referenceType.includes("contact")) return referenceId ? `/contacts/${referenceId}` : "/contacts";
  if (referenceType.includes("agent")) return referenceId ? `/agents/${referenceId}` : "/agents";
  if (referenceType.includes("campaign")) return "/campaigns";
  if (referenceType.includes("call") || referenceType.includes("conversation")) return "/calls";
  if (referenceType.includes("calendar")) return "/calendars";
  if (referenceType.includes("billing") || referenceType.includes("invoice") || referenceType.includes("payment")) return "/settings#billing";
  if (referenceType.includes("support")) return "/supports";
  return null;
};

export default function NotificationsIcon() {
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const { str } = useLanguage();

  const [opened, setOpened] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [total, setTotal] = useState(0);
  const [unread, setUnread] = useState(0);
  const [page, setPage] = useState(0);
  const [limit] = useState(DEFAULT_LIMIT);
  const [status, setStatus] = useState<NotificationStatusFilter>("all");
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [type, setType] = useState<string | null>(null);
  const [companies, setCompanies] = useState<any[]>([]);
  const [loadingReadIds, setLoadingReadIds] = useState<Record<string, boolean>>({});
  const [reloadTick, setReloadTick] = useState(0);

  const readableTypes = useMemo(
    () => Array.from(new Set(items.map((item) => item?.type).filter(Boolean))) as string[],
    [items]
  );
  const companyOptions = useMemo(() => {
    return companies
      .map((company) => {
        const value = company?.id || company?._id;
        return value ? { value, label: getCompanyLabel(company) } : null;
      })
      .filter(Boolean) as { value: string; label: string }[];
  }, [companies]);

  const companyFilterEnabled = companyOptions.length > 1;
  const totalPages = Math.max(1, Math.ceil((total || 0) / limit));

  useEffect(() => {
    setPage(0);
  }, [status, companyId, type]);

  useEffect(() => {
    if (!opened) return;

    const loadCompanies = async () => {
      try {
        const res = await GET_ALL_COMPANIES();
        const list = Array.isArray(res?.companies) ? res.companies : Array.isArray(res) ? res : [];
        setCompanies(list);
      } catch {
        setCompanies([]);
      }
    };

    loadCompanies();
  }, [opened]);

  useEffect(() => {
    if (!opened) return;

    const loadNotifications = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await GET_NOTIFICATIONS({
          page,
          limit,
          read: status === "all" ? undefined : status === "read",
          companyId: companyId || undefined,
          type: type || undefined,
        });

        setItems(response?.items || []);
        setTotal(response?.total || 0);
        setUnread(response?.unread || 0);
      } catch (err: any) {
        setError(err?.message || str("NOTIFICATIONS_CENTER.ERROR"));
      } finally {
        setLoading(false);
      }
    };

    loadNotifications();
  }, [opened, page, limit, status, companyId, type, reloadTick, str]);

  const handleMarkRead = async (notification: NotificationItem) => {
    if (!notification?.id || notification?.read || loadingReadIds[notification.id]) return;

    const previousItems = items;
    const previousUnread = unread;

    setLoadingReadIds((current) => ({ ...current, [notification.id]: true }));
    setItems((current) => current.map((item) => (item.id === notification.id ? { ...item, read: true, read_at: item.read_at || moment().toISOString() } : item)));
    setUnread((current) => Math.max(0, current - 1));

    try {
      const updated = await READ_NOTIFICATION(notification.id);
      setItems((current) => current.map((item) => (item.id === updated.id ? { ...item, ...updated } : item)));
    } catch (err: any) {
      setItems(previousItems);
      setUnread(previousUnread);
      notifications.show({ message: err?.message || str("NOTIFICATIONS_CENTER.ERROR"), color: "red" });
    } finally {
      setLoadingReadIds((current) => {
        const next = { ...current };
        delete next[notification.id];
        return next;
      });
    }
  };

  const handleClick = async (notification: NotificationItem) => {
    const href = resolveNotificationHref(notification);

    if (!notification.read) {
      await handleMarkRead(notification);
    }

    setOpened(false);

    if (href) {
      navigate(href);
    }
  };

  return (
    <Popover opened={opened} onChange={setOpened} position="bottom-end" withArrow shadow="xl" width={460}>
      <Popover.Target>
        <ActionIcon
          onClick={() => setOpened((current) => !current)}
          size={36}
          variant="transparent"
          color="gray.0"
          aria-label={str("NOTIFICATIONS_CENTER.TITLE")}
          style={{ position: "relative" }}
        >
          <FaBell />
          {unread > 0 && (
            <Badge size="sm" color="yellow" variant="filled" style={{ position: "absolute", top: -6, right: -8, pointerEvents: "none" }}>
              {unread > 99 ? "99+" : unread}
            </Badge>
          )}
        </ActionIcon>
      </Popover.Target>
      <Popover.Dropdown style={{ backgroundColor: theme.colors.dark[9], borderColor: theme.colors.dark[7] }}>
        <Stack gap="sm" w={420} maw="90vw">
          <Group justify="space-between" align="flex-start">
            <Box>
              <Text fw={700} c="gray.0">
                {str("NOTIFICATIONS_CENTER.TITLE")}
              </Text>
              <Text size="xs" c="gray.4">
                {unread} {str("NOTIFICATIONS_CENTER.UNREAD")}
              </Text>
            </Box>
            <Badge variant="light" color="gray">
              {total}
            </Badge>
          </Group>

          <Tabs value={status} onChange={(value) => setStatus((value as NotificationStatusFilter) || "all")}>
            <Tabs.List>
              <Tabs.Tab value="all">{str("NOTIFICATIONS_CENTER.ALL")}</Tabs.Tab>
              <Tabs.Tab value="unread">{str("NOTIFICATIONS_CENTER.UNREAD")}</Tabs.Tab>
              <Tabs.Tab value="read">{str("NOTIFICATIONS_CENTER.READ")}</Tabs.Tab>
            </Tabs.List>
          </Tabs>

          <Group grow align="flex-start">
            {companyFilterEnabled && (
              <Select
                value={companyId || ""}
                onChange={(value) => setCompanyId(value || null)}
                data={[{ value: "", label: str("NOTIFICATIONS_CENTER.ALL") }, ...companyOptions]}
                placeholder={str("NOTIFICATIONS_CENTER.COMPANY")}
                allowDeselect={false}
              />
            )}
            {readableTypes.length > 0 && (
              <Select
                value={type || ""}
                onChange={(value) => setType(value || null)}
                data={[{ value: "", label: str("NOTIFICATIONS_CENTER.ALL") }, ...readableTypes.map((itemType) => ({ value: itemType, label: itemType }))]}
                placeholder={str("NOTIFICATIONS_CENTER.TYPE")}
                allowDeselect={false}
              />
            )}
          </Group>

          {error ? (
            <Alert color="red" title={str("NOTIFICATIONS_CENTER.ERROR")}>
              <Group justify="space-between" align="center">
                <Text size="sm">{error}</Text>
                <Button size="xs" variant="light" onClick={() => setReloadTick((current) => current + 1)}>
                  {str("NOTIFICATIONS_CENTER.RETRY")}
                </Button>
              </Group>
            </Alert>
          ) : null}

          <Paper p="xs" withBorder style={{ backgroundColor: theme.colors.dark[8], borderColor: theme.colors.dark[7] }}>
            <ScrollArea h={360} offsetScrollbars>
              <Stack gap="xs">
                {loading ? (
                  <Group justify="center" py="lg">
                    <Loader size="sm" />
                  </Group>
                ) : items.length === 0 ? (
                  <Box py="xl" ta="center">
                    <Text c="gray.4">{str("NOTIFICATIONS_CENTER.EMPTY")}</Text>
                  </Box>
                ) : (
                  items.map((notification) => {
                    const companyName = notification?.company?.name || notification?.company?.title;
                    const read = Boolean(notification?.read);
                    const href = resolveNotificationHref(notification);

                    return (
                      <UnstyledButton
                        key={notification.id}
                        onClick={() => handleClick(notification)}
                        style={{
                          width: "100%",
                          textAlign: "left",
                          padding: 12,
                          borderRadius: 12,
                          backgroundColor: read ? theme.colors.dark[8] : theme.colors.dark[7],
                          border: `1px solid ${read ? theme.colors.dark[7] : theme.colors.yellow[7]}`,
                          opacity: loadingReadIds[notification.id] ? 0.7 : 1,
                        }}
                      >
                        <Stack gap={6}>
                          <Group justify="space-between" align="flex-start" wrap="nowrap">
                            <Box style={{ minWidth: 0, flex: 1 }}>
                              <Text fw={read ? 500 : 700} c="gray.0" lineClamp={1}>
                                {notification.title}
                              </Text>
                              <Text size="sm" c="gray.4" lineClamp={2}>
                                {notification.message}
                              </Text>
                            </Box>
                            <Group gap={6} wrap="nowrap">
                              {!read && <Badge color="yellow" variant="light">{str("NOTIFICATIONS_CENTER.UNREAD")}</Badge>}
                              {notification.type && <Badge variant="light">{notification.type}</Badge>}
                            </Group>
                          </Group>

                          <Group justify="space-between" align="center">
                            <Group gap="xs">
                              {companyName ? <Text size="xs" c="gray.5">{companyName}</Text> : null}
                              {href ? <Text size="xs" c="gray.5">{str("NOTIFICATIONS_CENTER.VIEW")}</Text> : null}
                            </Group>
                            <Text size="xs" c="gray.5">
                              {notification.created_at ? moment(notification.created_at).format("DD/MM/YYYY HH:mm") : ""}
                            </Text>
                          </Group>
                        </Stack>
                      </UnstyledButton>
                    );
                  })
                )}
              </Stack>
            </ScrollArea>
          </Paper>

          {totalPages > 1 && (
            <Group justify="flex-end">
              <Pagination value={page + 1} onChange={(value) => setPage(value - 1)} total={totalPages} size="sm" />
            </Group>
          )}
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
